import {
  Component,
  inject,
  signal,
  AfterViewInit,
  OnInit,
  OnDestroy,
  PLATFORM_ID,
  ViewEncapsulation,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { PricingComponent } from './pricing/pricing.component';
import { ApplyFormComponent } from './apply-form/apply-form.component';
import { LanguageService } from '../../core/services/language.service';
import { TranslatePipe } from '@ngx-translate/core';

interface Particle {
  left: string;
  bottom: string;
  duration: number;
  delay: number;
  scale: number;
}

@Component({
  selector: 'app-home',
  standalone: true,
  imports: [CommonModule, RouterModule, PricingComponent, ApplyFormComponent, TranslatePipe],
  templateUrl: './home.component.html',
  styleUrl: './home.component.css',
  encapsulation: ViewEncapsulation.None,
})
export class HomeComponent implements OnInit, AfterViewInit, OnDestroy {
  private platformId = inject(PLATFORM_ID);
  languageService = inject(LanguageService);
  langOpen = signal(false);
  readonly langs = [
    { code: 'uz', label: "O'zbek", flag: '🇺🇿' },
    { code: 'uz-cyrl', label: 'Ўзбек', flag: '🇺🇿' },
    { code: 'ru', label: 'Русский', flag: '🇷🇺' },
    { code: 'en', label: 'English', flag: '🇬🇧' },
  ];

  selectLang(code: string) {
    this.languageService.switchLanguage(code);
    this.langOpen.set(false);
  }

  // Floating call button
  callMenuOpen = signal(false);
  readonly phones = [
    { label: '+998 99 606 36 62', href: 'tel:+998996063662' },
    { label: '+998 94 876 80 05', href: 'tel:+998948768005' },
  ];

  // MacBook scene
  macOpen = signal(false);
  activeModule = signal(0);
  private paused = false;
  private cycleTimer: ReturnType<typeof setInterval> | null = null;
  readonly TOTAL_MODULES = 7;

  // Particles
  heroParticles: Particle[] = [];
  sceneParticles: Particle[] = [];

  // Module metadata
  moduleItems = [
    {
      id: 'pos',
      title: 'Kassa — bir zumda ishlaydi.',
      sub: "Tez, oflayn-xavfsiz, O'zbekistondagi har bir to'lov tizimi bilan integratsiya qilingan kassa moduli.",
      label: 'Kassa',
    },
    {
      id: 'warehouse',
      title: 'Ombor — har doim sinxron.',
      sub: "Barcha filiallar bo'yicha qoldiqlar, avtomatik xarid — sotiladigan narsa hech qachon tugamaydi.",
      label: 'Ombor',
    },
    {
      id: 'employees',
      title: 'Xodimlar va davomat — bitta panelda.',
      sub: "QR check-in, smenalar, oyliklar va samaradorlik — kim qachon kelganini sekundlarda ko'ring.",
      label: 'Davomat',
    },
    {
      id: 'analytics',
      title: 'Tahlillar — qarorlarni boshqaradi.',
      sub: "Har bir tranzaksiya — bu ma'lumot. Marja, tovar aylanmasi, tezlik — bir nigohda.",
      label: 'Tahlil',
    },
    {
      id: 'debt',
      title: 'Qarzlar — nihoyat tartibli.',
      sub: 'Mijoz qarzlarini kuzating, eslatmalarni avtomatlashtiring, kerakli pulning 94 % ini qaytaring.',
      label: 'Qarzlar',
    },
    {
      id: 'reports',
      title: 'Hisobotlar — buxgalter sevadi.',
      sub: "Foyda-zarar, soliq xulosalari, pul oqimi — avtomatik generatsiya va jadval bo'yicha yuboriladi.",
      label: 'Hisobot',
    },
    {
      id: 'live',
      title: "Real-vaqt monitoring — to'xtamaydi.",
      sub: "Har bir kassani, har bir filialni, har bir tranzaksiyani — sodir bo'lganda darrov ko'ring.",
      label: 'Jonli',
    },
  ];

  integrations = [
    ['Click', 'CL'],
    ['Payme', 'PM'],
    ['Uzum', 'UZ'],
    ['Humo', 'HM'],
    ['Apple Pay', 'AP'],
    ['Google Pay', 'GP'],
    ['Visa', 'VS'],
    ['Mastercard', 'MC'],
    ['Stripe', 'ST'],
    ['1C', '1C'],
    ['e-Fiscal', 'EF'],
    ['Telegram', 'TG'],
    ['Slack', 'SL'],
    ['Yandex', 'YA'],
    ['DHL', 'DH'],
    ['FedEx', 'FX'],
  ];
  get integrationsDoubled() {
    return [...this.integrations, ...this.integrations];
  }

  // SVG data for sparklines / bars
  readonly spark14 = [40, 42, 44, 48, 46, 52, 56, 60, 58, 62, 64, 70, 72, 78];
  readonly spark12 = [20, 24, 28, 26, 32, 38, 42, 40, 48, 52, 58, 64];
  readonly latency = [400, 420, 380, 460, 410, 440, 390, 420, 400, 380, 410, 420, 400, 410, 395];
  readonly bars7 = [58, 64, 72, 68, 78, 86, 92];
  readonly analytics30 = [
    42, 38, 45, 52, 48, 55, 60, 58, 64, 52, 68, 72, 66, 74, 80, 76, 82, 78, 85, 90, 86, 92, 88, 95,
    100, 94, 108, 102, 118, 124,
  ];
  readonly phoneData = [40, 44, 42, 50, 54, 58, 62, 60, 68, 72, 76, 82, 86, 90];
  readonly repReports = [
    {
      t: 'P&L statement',
      s: 'Monthly · auto-sent to CFO',
      d: [22, 28, 25, 32, 38, 42, 48, 52, 50, 58, 64, 72],
    },
    {
      t: 'Tax summary',
      s: 'Quarterly · VAT-12 compliant',
      d: [40, 38, 42, 45, 48, 44, 50, 52, 56, 54, 60, 62],
    },
    {
      t: 'Inventory turnover',
      s: 'Weekly · by location',
      d: [12, 14, 18, 16, 22, 24, 20, 26, 30, 28, 34, 38],
    },
    { t: 'Cash flow', s: 'Daily · realtime', d: [60, 58, 62, 65, 70, 68, 72, 76, 74, 78, 82, 86] },
    {
      t: 'Top SKUs',
      s: 'Weekly · category breakdown',
      d: [10, 14, 12, 18, 22, 20, 26, 30, 28, 34, 38, 42],
    },
    {
      t: 'Customer LTV',
      s: 'Monthly · cohort view',
      d: [30, 34, 32, 38, 40, 42, 46, 50, 48, 52, 58, 60],
    },
  ];

  // SVG constants
  readonly donutCircumference = 2 * Math.PI * 32;

  constructor() {
    this.heroParticles = this.makeParticles(14);
    this.sceneParticles = this.makeParticles(8);
  }

  ngOnInit() {
    if (isPlatformBrowser(this.platformId)) {
      document.body.classList.add('landing-body');
    }
  }

  ngAfterViewInit() {
    if (isPlatformBrowser(this.platformId)) {
      this.initReveal();
      this.initMacObserver();
      this.initCounters();
    }
  }

  ngOnDestroy() {
    if (this.cycleTimer) clearInterval(this.cycleTimer);
    if (isPlatformBrowser(this.platformId)) {
      document.body.classList.remove('landing-body');
    }
  }

  // ── MacBook ──────────────────────────────────────────────────────────────
  goto(i: number) {
    this.activeModule.set(i);
    this.paused = true;
    setTimeout(() => {
      this.paused = false;
    }, 6000);
  }
  nextModule() {
    this.goto((this.activeModule() + 1) % this.TOTAL_MODULES);
  }

  scrollTo(id: string) {
    if (isPlatformBrowser(this.platformId)) {
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    }
  }

  // ── Internals ────────────────────────────────────────────────────────────
  private makeParticles(count: number): Particle[] {
    return Array.from({ length: count }, () => ({
      left: `${Math.random() * 100}%`,
      bottom: `${Math.random() * 40}%`,
      duration: 8 + Math.random() * 8,
      delay: -Math.random() * 8,
      scale: 0.6 + Math.random() * 0.8,
    }));
  }

  private initReveal() {
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add('in');
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.18 },
    );
    document.querySelectorAll('.reveal').forEach((el) => io.observe(el));
  }

  private initMacObserver() {
    const section = document.querySelector('.scene');
    if (!section) return;
    const io = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          this.macOpen.set(true);
          this.cycleTimer = setInterval(() => {
            if (!this.paused) this.activeModule.update((i) => (i + 1) % this.TOTAL_MODULES);
          }, 4200);
          io.disconnect();
        }
      },
      { threshold: 0.25 },
    );
    io.observe(section);
  }

  private initCounters() {
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const el = entry.target as HTMLElement;
          const target = parseFloat(el.dataset['counter']!);
          const decimals = parseInt(el.dataset['decimals'] || '0');
          const suffix = el.dataset['suffix'] || '';
          const prefix = el.dataset['prefix'] || '';
          const start = performance.now();
          const tick = (t: number) => {
            const k = Math.min(1, (t - start) / 1400);
            const eased = 1 - Math.pow(1 - k, 3);
            el.textContent = `${prefix}${(target * eased).toFixed(decimals)}${suffix}`;
            if (k < 1) requestAnimationFrame(tick);
          };
          requestAnimationFrame(tick);
          io.unobserve(el);
        });
      },
      { threshold: 0.3 },
    );
    document.querySelectorAll('[data-counter]').forEach((el) => io.observe(el));
  }

  // ── SVG helpers ──────────────────────────────────────────────────────────
  sparkPoints(data: number[], h = 56, w = 240): string {
    const max = Math.max(...data),
      min = Math.min(...data);
    const norm = (v: number) => h - 6 - ((v - min) / Math.max(max - min, 0.001)) * (h - 14);
    const step = w / Math.max(data.length - 1, 1);
    return data.map((v, i) => `${(i * step).toFixed(1)},${norm(v).toFixed(1)}`).join(' ');
  }
  sparkArea(data: number[], h = 56, w = 240): string {
    return `M0,${h} L ${this.sparkPoints(data, h, w)} L${w},${h} Z`;
  }
  barsHeights(data: number[]): number[] {
    const max = Math.max(...data);
    return data.map((v) => (v / max) * 100);
  }
  donutOffset(value: number): number {
    return this.donutCircumference - (value / 100) * this.donutCircumference;
  }
}
